import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Tooltip, Legend, Filler,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import { Lock, TrendingUp, Users, Package, IndianRupee, RefreshCw, Search, Download, ChevronDown, X, Filter, Receipt, Percent } from "lucide-react";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtCompact = n => {
  n = n || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtMonth = m => m ? new Date(m + "-01").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "-";
const _fyOf = ym => {
  if (!ym || ym.length < 7) return "";
  const y = parseInt(ym.slice(0, 4), 10);
  const m = parseInt(ym.slice(5, 7), 10);
  if (!y || !m) return "";
  const s = m >= 4 ? y : y - 1;
  return `${s}-${String(s + 1).slice(-2)}`;
};

const PALETTE = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f43f5e", "#6366f1", "#14b8a6", "#eab308", "#a855f7"];

export default function LockedInvoicesAnalytics() {
  const [data, setData] = useState(null);
  const [filterOpts, setFilterOpts] = useState({ batches: [], financial_years: [], fy_months: {}, billing_months: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // Filters
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [batchDdOpen, setBatchDdOpen] = useState(false);
  const [monthDdOpen, setMonthDdOpen] = useState(false);
  const [batchSearch, setBatchSearch] = useState("");
  const batchDdRef = useRef(null);
  const monthDdRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (batchDdRef.current && !batchDdRef.current.contains(e.target)) setBatchDdOpen(false);
      if (monthDdRef.current && !monthDdRef.current.contains(e.target)) setMonthDdOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchFilterOpts = async () => {
    try {
      const r = await axios.get(`${API}/analytics/locked-invoices/filters`);
      setFilterOpts(r.data);
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      selectedBatches.forEach(id => params.append("batch_ids", id));
      selectedMonths.forEach(m => params.append("billing_months", m));
      if (selectedFY) params.append("financial_year", selectedFY);
      const r = await axios.get(`${API}/analytics/locked-invoices?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      setError(e.response?.status === 404 ? "Analytics endpoint not available yet. Please redeploy the backend." : (e.response?.data?.detail || e.message || "Failed to load analytics"));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchFilterOpts(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selectedBatches, selectedFY, selectedMonths]);

  // Available months depend on selected FY (if any)
  const availableMonths = useMemo(() => {
    if (selectedFY && filterOpts.fy_months?.[selectedFY]) return filterOpts.fy_months[selectedFY];
    return filterOpts.billing_months || [];
  }, [selectedFY, filterOpts]);

  // When FY changes, prune selected months to those valid in the new FY
  useEffect(() => {
    if (!selectedFY) return;
    const valid = new Set(filterOpts.fy_months?.[selectedFY] || []);
    setSelectedMonths(ms => ms.filter(m => valid.has(m)));
    // eslint-disable-next-line
  }, [selectedFY]);

  const filteredBatchOpts = useMemo(() => {
    const q = batchSearch.toLowerCase();
    return (filterOpts.batches || []).filter(b => {
      if (selectedFY && b.financial_year !== selectedFY) return false;
      if (!q) return true;
      return (b.name || "").toLowerCase().includes(q) || (b.billing_month || "").includes(q);
    });
  }, [filterOpts.batches, batchSearch, selectedFY]);

  const toggleBatch = id => setSelectedBatches(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleMonth = m => setSelectedMonths(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);
  const clearAllFilters = () => { setSelectedBatches([]); setSelectedFY(""); setSelectedMonths([]); setBatchSearch(""); };
  const activeFilterCount = selectedBatches.length + (selectedFY ? 1 : 0) + selectedMonths.length;

  const s = data?.summary || {};
  const batches = useMemo(() => (data?.batches || []).filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (b.name || "").toLowerCase().includes(q) ||
           (b.projects || []).some(p => p.toLowerCase().includes(q)) ||
           (b.resources || []).some(r => (typeof r === "string" ? r : `${r.name} ${r.email}`).toLowerCase().includes(q));
  }), [data, search]);

  const exportCsv = () => {
    if (!data?.batches?.length) return;
    const rows = [["Batch", "Month", "Billing Month", "Locked At", "Tasks", "Amount", "Resources", "Projects", "Seasons", "Episodes", "Task Types"]];
    data.batches.forEach(b => {
      rows.push([
        b.name || "", b.month || "", b.billing_month || "", b.locked_at || "",
        b.task_count, b.total_amount, b.resource_count, b.project_count, b.season_count, b.episode_count,
        (b.task_types || []).join("|"),
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `locked-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart datasets
  const byMonthChart = useMemo(() => ({
    labels: (data?.by_month || []).map(m => fmtMonth(m.month)),
    datasets: [{
      label: "Amount Locked",
      data: (data?.by_month || []).map(m => m.amount),
      backgroundColor: "#10b98122",
      borderColor: "#10b981",
      borderWidth: 2,
      borderRadius: 6,
    }],
  }), [data]);

  const byResourceChart = useMemo(() => {
    const top = (data?.by_resource || []).slice(0, 15);
    return {
      labels: top.map(r => r.name || r.resource),
      datasets: [{
        label: "Amount",
        data: top.map(r => r.amount),
        backgroundColor: "#3b82f6cc",
        borderColor: "#3b82f6",
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  }, [data]);

  const byTaskTypeChart = useMemo(() => {
    const items = (data?.by_task_type || []).slice(0, 10);
    return {
      labels: items.map(t => t.task_type),
      datasets: [{
        data: items.map(t => t.amount),
        backgroundColor: items.map((_, i) => PALETTE[i % PALETTE.length]),
        borderColor: "#fff",
        borderWidth: 2,
      }],
    };
  }, [data]);

  const byProjectChart = useMemo(() => {
    const items = (data?.by_project || []).slice(0, 10);
    return {
      labels: items.map(p => p.project.length > 22 ? p.project.slice(0, 22) + "…" : p.project),
      datasets: [{
        data: items.map(p => p.amount),
        backgroundColor: items.map((_, i) => PALETTE[(i + 3) % PALETTE.length]),
        borderColor: "#fff",
        borderWidth: 2,
      }],
    };
  }, [data]);

  const cumulativeChart = useMemo(() => ({
    labels: (data?.cumulative || []).map(c => fmtMonth(c.month)),
    datasets: [
      {
        label: "Cumulative",
        data: (data?.cumulative || []).map(c => c.cumulative_amount),
        borderColor: "#8b5cf6",
        backgroundColor: "#8b5cf622",
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        borderWidth: 2,
        yAxisID: "y",
      },
      {
        label: "Monthly",
        data: (data?.cumulative || []).map(c => c.monthly_amount),
        borderColor: "#10b981",
        backgroundColor: "transparent",
        borderDash: [5, 4],
        tension: 0.3,
        pointRadius: 2,
        borderWidth: 1.5,
        yAxisID: "y",
      },
    ],
  }), [data]);

  // Per-resource monthly trendline — top 8 earners
  const trendlineChart = useMemo(() => {
    const months = data?.months_axis || [];
    const top = (data?.by_resource || []).slice(0, 8);
    return {
      labels: months.map(m => fmtMonth(m)),
      datasets: top.map((r, i) => ({
        label: r.name || r.resource,
        data: (r.monthly || []).map(p => p.amount),
        borderColor: PALETTE[i % PALETTE.length],
        backgroundColor: PALETTE[i % PALETTE.length] + "22",
        tension: 0.3,
        pointRadius: 3,
        borderWidth: 2,
        fill: false,
      })),
    };
  }, [data]);

  const chartOpts = { plugins: { legend: { labels: { font: { size: 11 } } } }, maintainAspectRatio: false, responsive: true };
  const barOpts = { ...chartOpts, plugins: { ...chartOpts.plugins, legend: { display: false } }, scales: { y: { ticks: { font: { size: 10 }, callback: v => fmtCompact(v) } }, x: { ticks: { font: { size: 10 } } } } };
  const hOpts = { ...barOpts, indexAxis: "y", scales: { x: { ticks: { font: { size: 10 }, callback: v => fmtCompact(v) } }, y: { ticks: { font: { size: 10 } } } } };
  const donutOpts = { ...chartOpts, plugins: { ...chartOpts.plugins, legend: { position: "right", labels: { font: { size: 10 }, boxWidth: 10 } } }, cutout: "55%" };

  if (loading) return <div className="p-8 flex items-center justify-center h-96 text-slate-400 text-sm">Loading analytics…</div>;

  return (
    <div className="p-4 sm:p-6 max-w-full overflow-x-hidden" data-testid="locked-analytics-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
            <span className="truncate">Locked Invoices Analytics</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Comprehensive metrics across all locked invoice batches</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-analytics" className="h-9 sm:h-8">
            <RefreshCw className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.batches?.length} data-testid="export-csv" className="h-9 sm:h-8">
            <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">CSV</span>
          </Button>
        </div>
      </div>

      {/* Filter bar: Financial Year + Financial Month(s) + Batches multi-select */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap" data-testid="locked-filter-bar">
        <div className="flex items-center gap-1.5 text-slate-600 text-xs font-semibold basis-full sm:basis-auto"><Filter className="w-3.5 h-3.5 text-slate-400" />Filters</div>

        {/* Financial Year */}
        <div className="flex-1 sm:flex-initial min-w-[120px]">
          <select
            data-testid="filter-financial-year"
            value={selectedFY}
            onChange={e => setSelectedFY(e.target.value)}
            className="h-10 sm:h-8 px-2 text-[13px] sm:text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 cursor-pointer w-full sm:min-w-[130px]"
          >
            <option value="">All Financial Years</option>
            {(filterOpts.financial_years || []).map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
          </select>
        </div>

        {/* Financial Months multi-select */}
        <div ref={monthDdRef} className="relative flex-1 sm:flex-initial min-w-[130px]">
          <button
            type="button"
            data-testid="filter-months-trigger"
            onClick={() => setMonthDdOpen(o => !o)}
            className="h-10 sm:h-8 px-2.5 text-[13px] sm:text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 flex items-center gap-1.5 w-full sm:min-w-[160px] justify-between"
          >
            {selectedMonths.length === 0 ? <span className="text-slate-400">All Months</span> : <span className="font-medium">{selectedMonths.length} month{selectedMonths.length > 1 ? "s" : ""}</span>}
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${monthDdOpen ? "rotate-180" : ""}`} />
          </button>
          {monthDdOpen && (
            <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg w-56 max-h-72 overflow-y-auto">
              <div className="p-1.5 border-b flex justify-between items-center sticky top-0 bg-white">
                <span className="text-[10px] text-slate-400">{selectedMonths.length}/{availableMonths.length}</span>
                <div className="flex gap-1.5">
                  <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setSelectedMonths([...availableMonths])}>Select all</button>
                  {selectedMonths.length > 0 && <button className="text-[10px] text-slate-500 hover:underline" onClick={() => setSelectedMonths([])}>Clear</button>}
                </div>
              </div>
              {availableMonths.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No months available</p>}
              {availableMonths.map(m => (
                <label key={m} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-xs" data-testid={`month-option-${m}`}>
                  <input type="checkbox" checked={selectedMonths.includes(m)} onChange={() => toggleMonth(m)} className="w-3.5 h-3.5 accent-emerald-600" />
                  <span className="text-slate-700">{fmtMonth(m)}</span>
                  <span className="ml-auto text-[10px] text-slate-400">FY {_fyOf(m)}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Batches multi-select */}
        <div ref={batchDdRef} className="relative basis-full sm:basis-0 sm:flex-1 min-w-[200px] sm:max-w-[420px]">
          <button
            type="button"
            data-testid="filter-batches-trigger"
            onClick={() => setBatchDdOpen(o => !o)}
            className="h-10 sm:h-8 px-2.5 text-[13px] sm:text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 flex items-center gap-1.5 w-full justify-between"
          >
            <span className="flex items-center gap-1.5 truncate"><Lock className="w-3.5 h-3.5 text-slate-400" />{selectedBatches.length === 0 ? <span className="text-slate-400">All Batches</span> : <span className="font-medium">{selectedBatches.length} batch{selectedBatches.length > 1 ? "es" : ""} selected</span>}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${batchDdOpen ? "rotate-180" : ""}`} />
          </button>
          {batchDdOpen && (
            <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-96 flex flex-col">
              <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                <Input autoFocus data-testid="filter-batches-search" placeholder="Search batch name or month…" className="h-7 text-xs" value={batchSearch} onChange={e => setBatchSearch(e.target.value)} />
                <div className="flex items-center justify-between mt-1.5 px-0.5">
                  <span className="text-[10px] text-slate-400">{selectedBatches.length} of {filteredBatchOpts.length} selected{selectedFY ? ` · FY ${selectedFY}` : ""}</span>
                  <div className="flex gap-2">
                    <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setSelectedBatches(filteredBatchOpts.map(b => b.id))}>Select all{batchSearch || selectedFY ? " (filtered)" : ""}</button>
                    {selectedBatches.length > 0 && <button className="text-[10px] text-slate-500 hover:underline" onClick={() => setSelectedBatches([])}>Clear</button>}
                  </div>
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {filteredBatchOpts.length === 0 ? <p className="text-xs text-slate-400 py-4 text-center">No batches match</p> : filteredBatchOpts.map(b => {
                  const checked = selectedBatches.includes(b.id);
                  return (
                    <label key={b.id} data-testid={`batch-option-${b.id}`} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-xs">
                      <input type="checkbox" checked={checked} onChange={() => toggleBatch(b.id)} className="w-3.5 h-3.5 accent-emerald-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{b.name}</div>
                        <div className="text-[10px] text-slate-400 flex gap-2"><span>{fmtMonth(b.billing_month)}</span><span>FY {b.financial_year}</span></div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} data-testid="clear-filters" className="text-xs text-slate-500 hover:text-red-500 h-8">
            <X className="w-3.5 h-3.5 mr-1" />Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Always-visible: KPI cards (including invoice tracking) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3 mb-4">
        {[
          { label: "Invoices Generated", value: (s.invoice_count || 0).toLocaleString(), icon: <Receipt className="w-4 h-4" />, color: "from-emerald-500 to-teal-600", testid: "kpi-invoice-count" },
          { label: "Total Subtotal", value: fmtCompact(s.invoice_subtotal), icon: <IndianRupee className="w-4 h-4" />, color: "from-blue-500 to-indigo-600", testid: "kpi-invoice-subtotal" },
          { label: "TDS Collected", value: fmtCompact(s.total_tds_collected), icon: <Percent className="w-4 h-4" />, color: "from-amber-500 to-orange-600", testid: "kpi-total-tds" },
          { label: "Grand Total Billed", value: fmtCompact(s.invoice_grand_total), icon: <TrendingUp className="w-4 h-4" />, color: "from-violet-500 to-purple-600", testid: "kpi-invoice-grand" },
          { label: "Effective TDS %", value: `${(s.effective_tds_pct || 0).toFixed(2)}%`, icon: <Percent className="w-4 h-4" />, color: "from-pink-500 to-rose-600", testid: "kpi-effective-tds" },
        ].map((k, i) => (
          <div key={i} data-testid={k.testid} className={`relative overflow-hidden rounded-2xl p-3 sm:p-4 bg-gradient-to-br ${k.color} text-white shadow-sm`}>
            <div className="flex items-start justify-between">
              <p className="text-[9px] sm:text-[10px] uppercase tracking-wider opacity-80 font-medium leading-tight">{k.label}</p>
              <span className="opacity-70">{k.icon}</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold mt-1.5 sm:mt-2 truncate">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Generated Invoices — Per-Resource Tracker */}
      {(data?.by_resource_invoices || []).length > 0 && (
        <Card className="mb-4" data-testid="by-resource-invoices-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              Officially Generated Invoices — Per Resource
            </CardTitle>
            <p className="text-[11px] text-slate-400 mt-0.5">Count and TDS tracked from invoices created via the Invoice Generator. Filters apply on invoice date.</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="invoice-resource-table">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Resource</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Invoices</th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Subtotal</th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">TDS Collected</th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Grand Total</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Eff. TDS %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.by_resource_invoices || []).map((r, i) => (
                    <tr key={r.email} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`invoice-resource-row-${i}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{r.name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{r.email}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{r.invoice_count}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmt(r.subtotal)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-amber-700">{fmt(r.tds_amount)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(r.grand_total)}</td>
                      <td className="px-3 py-2 text-center text-slate-600 font-mono text-[11px]">{(r.effective_tds_pct || 0).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr className="font-semibold">
                    <td className="px-3 py-2 text-slate-700 text-[11px] uppercase tracking-wide">Total</td>
                    <td className="px-3 py-2 text-center text-slate-800">{s.invoice_count || 0}</td>
                    <td className="px-3 py-2 text-right text-slate-800">{fmt(s.invoice_subtotal)}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{fmt(s.total_tds_collected)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmt(s.invoice_grand_total)}</td>
                    <td className="px-3 py-2 text-center text-slate-700 font-mono text-[11px]">{(s.effective_tds_pct || 0).toFixed(2)}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generated Invoices List + Monthly TDS */}
      {(data?.generated_invoices || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Card className="lg:col-span-2" data-testid="generated-invoices-card">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-600" />
                Generated Invoices ({(data?.generated_invoices || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-96">
                <table className="w-full text-xs" data-testid="invoices-list-table">
                  <thead className="bg-slate-50 border-y border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Invoice #</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Resource</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Subtotal</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">TDS</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Grand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.generated_invoices || []).map(inv => (
                      <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`invoice-row-${inv.id}`}>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{inv.invoice_number}</td>
                        <td className="px-3 py-2 text-slate-500">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : "—"}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-700">{inv.resource_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{inv.resource_email}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">{fmt(inv.subtotal)}</td>
                        <td className="px-3 py-2 text-right text-amber-700 font-medium">{fmt(inv.tds_amount)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{fmt(inv.grand_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="tds-by-month-card">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Percent className="w-4 h-4 text-amber-600" />
                Monthly TDS Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-96">
                <table className="w-full text-xs" data-testid="tds-month-table">
                  <thead className="bg-slate-50 border-y border-slate-200 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Month</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">#</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.invoices_by_month || []).map(m => (
                      <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-3 py-2 text-slate-700">{fmtMonth(m.month)}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{m.invoice_count}</td>
                        <td className="px-3 py-2 text-right text-amber-700 font-semibold">{fmt(m.tds_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(!data || s.batch_count === 0) ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100" data-testid="locked-analytics-empty">
          <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">{error ? "Unable to load analytics" : "No locked batches yet"}</p>
          <p className="text-sm text-slate-400 mt-1">{error || "Lock an invoice batch to see batch-level analytics below."}</p>
          {error && (
            <Button size="sm" className="mt-4" onClick={fetchData} data-testid="retry-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Retry</Button>
          )}
        </div>
      ) : (
        <>
          {/* KPI Cards — Locked Batches metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2.5 sm:gap-3 mb-6">
            {[
              { label: "Locked Batches", value: s.batch_count, icon: <Lock className="w-4 h-4" />, color: "from-emerald-500 to-teal-600", testid: "kpi-batches" },
              { label: "Locked Amount", value: fmtCompact(s.total_amount), icon: <IndianRupee className="w-4 h-4" />, color: "from-blue-500 to-indigo-600", testid: "kpi-amount" },
              { label: "Avg / Batch", value: fmtCompact(s.avg_batch_amount), icon: <TrendingUp className="w-4 h-4" />, color: "from-violet-500 to-purple-600", testid: "kpi-avg" },
              { label: "Total Tasks", value: (s.total_tasks || 0).toLocaleString(), icon: <Package className="w-4 h-4" />, color: "from-amber-500 to-orange-600", testid: "kpi-tasks" },
              { label: "Resources Paid", value: s.unique_resources, icon: <Users className="w-4 h-4" />, color: "from-pink-500 to-rose-600", testid: "kpi-resources" },
              { label: "Projects", value: s.unique_projects, icon: <Package className="w-4 h-4" />, color: "from-cyan-500 to-sky-600", testid: "kpi-projects" },
              { label: "Episodes", value: s.unique_episodes, icon: <Package className="w-4 h-4" />, color: "from-lime-500 to-green-600", testid: "kpi-episodes" },
            ].map((k, i) => (
              <div key={i} data-testid={k.testid} className={`relative overflow-hidden rounded-2xl p-3 sm:p-4 bg-gradient-to-br ${k.color} text-white shadow-sm`}>
                <div className="flex items-start justify-between">
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-wider opacity-80 font-medium leading-tight">{k.label}</p>
                  <span className="opacity-70">{k.icon}</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold mt-1.5 sm:mt-2 truncate">{k.value}</p>
              </div>
            ))}
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Card className="lg:col-span-2">
              <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Amount by Billing Month</CardTitle></CardHeader>
              <CardContent><div className="h-64"><Bar data={byMonthChart} options={barOpts} /></div></CardContent>
            </Card>
            <Card>
              <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">By Task Type</CardTitle></CardHeader>
              <CardContent><div className="h-64"><Doughnut data={byTaskTypeChart} options={donutOpts} /></div></CardContent>
            </Card>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Card className="lg:col-span-2">
              <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Top 15 Resources by Amount</CardTitle></CardHeader>
              <CardContent><div className="h-80"><Bar data={byResourceChart} options={hOpts} /></div></CardContent>
            </Card>
            <Card>
              <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Top Projects</CardTitle></CardHeader>
              <CardContent><div className="h-80"><Doughnut data={byProjectChart} options={donutOpts} /></div></CardContent>
            </Card>
          </div>

          {/* Cumulative trend */}
          <Card className="mb-4">
            <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Cumulative & Monthly Locked Volume</CardTitle></CardHeader>
            <CardContent><div className="h-64"><Line data={cumulativeChart} options={{ ...chartOpts, scales: { y: { ticks: { font: { size: 10 }, callback: v => fmtCompact(v) } }, x: { ticks: { font: { size: 10 } } } } }} /></div></CardContent>
          </Card>

          {/* Per-resource trendline (top 8 earners) */}
          {(data?.months_axis || []).length > 0 && (data?.by_resource || []).length > 0 && (
            <Card className="mb-4">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-semibold">Top Earners — Monthly Trendline</CardTitle>
                <p className="text-[11px] text-slate-400 mt-0.5">Month-over-month earnings for the top 8 resources by total amount</p>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <Line data={trendlineChart} options={{ ...chartOpts, plugins: { ...chartOpts.plugins, legend: { position: "bottom", labels: { font: { size: 10 }, boxWidth: 14, padding: 8 } } }, scales: { y: { ticks: { font: { size: 10 }, callback: v => fmtCompact(v) } }, x: { ticks: { font: { size: 10 } } } } }} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Batches table */}
          <Card className="mb-4">
            <CardHeader className="py-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-sm font-semibold">Locked Batches ({batches.length})</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input placeholder="Search batch, project, resource…" className="pl-8 h-8 text-xs" value={search} onChange={e => setSearch(e.target.value)} data-testid="batches-search" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="batches-table">
                  <thead className="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Batch</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Billing</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Projects</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Seasons</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Episodes</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Tasks</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Resources</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`batch-row-${b.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{b.name}</div>
                          <div className="text-[10px] text-slate-400">{b.locked_at ? new Date(b.locked_at).toLocaleDateString() : "-"}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div>{fmtMonth(b.billing_month || b.month)}</div>
                          {b.financial_year && <div className="text-[10px] text-violet-600 font-medium">FY {b.financial_year}</div>}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-700">{b.project_count}</td>
                        <td className="px-3 py-2 text-center">
                          {b.seasons?.length > 0 ? (
                            <div className="flex flex-wrap gap-0.5 justify-center max-w-[140px] mx-auto">
                              {b.seasons.slice(0, 4).map(s => <Badge key={s} className="bg-violet-100 text-violet-700 border-0 text-[9px] px-1.5 py-0">{s}</Badge>)}
                              {b.seasons.length > 4 && <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px] px-1 py-0">+{b.seasons.length - 4}</Badge>}
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center font-medium text-slate-700">{b.episode_count || "—"}</td>
                        <td className="px-3 py-2 text-center text-slate-700">{b.task_count}</td>
                        <td className="px-3 py-2 text-center text-slate-700">{b.resource_count}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(b.total_amount)}</td>
                      </tr>
                    ))}
                    {batches.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-400">No batches match</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Per-resource table */}
          <Card>
            <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Resources — Locked Earnings Breakdown</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="resources-table">
                  <thead className="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Resource</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Batches</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Projects</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Tasks</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Total Amount</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Avg/Task</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.by_resource || []).map((r, i) => (
                      <tr key={r.email || r.resource} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`resource-row-${i}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{r.name || r.resource}</div>
                          {r.email && r.email !== r.name && <div className="text-[10px] text-slate-400">{r.email}</div>}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">{r.batch_count}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{r.project_count}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{r.tasks}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmt(r.amount)}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{fmt(r.tasks > 0 ? r.amount / r.tasks : 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
